import {
  customizationsSpritePath,
  customizationsUIPath,
} from '@/app/constants';
import Rarities from '@/common/data/rarityTiers.json';
import { Customization, RarityTier } from '@/common/types/student';
import Customizations from '@/game/GameComponents/GameData/customizations.json';
import CustomizationsNPC from '@/game/GameComponents/GameData/customizationsNPC.json';
import { getCharacterInventory } from '@/game/GameComponents/PhaserGame/GameInstance';

export function getDollViewPath(customization: Customization): string {
  return `ui-${customization.texturePath}-small`;
}

export function getIconPath(customization: Customization): string {
  return `ui-${customization.texturePath}-large`;
}

export function getSpriteSheetPath(customization: Customization): string {
  return `${customization.texturePath}`;
}

export function getCustomizationFromId(id: number) {
  const customizations = Customizations as Customization[];
  return customizations.find((customization) => customization.id === id);
}

export function getNpcCustomizationFromId(id: number) {
  const customizations = CustomizationsNPC as Customization[];
  return customizations.find((customization) => customization.id === id);
}

export function hasUnlockedACustomizationInSlot(slot: string) {
  const unlockedItems = getCharacterInventory().unlockedItems;
  return (
    unlockedItems.find((i) => {
      const c = getCustomizationFromId(i);
      return c?.slot === slot;
    }) !== undefined
  );
}

export function hasLoadedCustomizationTextures(
  scene: Phaser.Scene,
  customization: Customization,
) {
  return (
    scene.textures.exists(getSpriteSheetPath(customization)) &&
    scene.textures.exists(getDollViewPath(customization)) &&
    scene.textures.exists(getIconPath(customization))
  );
}

export function loadCustomizationTextures(
  scene: Phaser.Scene,
  customization: Customization,
  onLoadComplete?: () => void,
) {
  let targetFilesLoaded = 0;
  const spriteSheetPath = getSpriteSheetPath(customization);
  const dollViewPath = getDollViewPath(customization);
  const iconPath = getIconPath(customization);

  if (onLoadComplete) {
    let filesLoaded = 0;
    scene.load.on('filecomplete-spritesheet-' + spriteSheetPath, () => {
      filesLoaded++;
      if (filesLoaded === targetFilesLoaded) {
        onLoadComplete();
      }
    });
    scene.load.on('filecomplete-image-' + dollViewPath, () => {
      filesLoaded++;
      if (filesLoaded === targetFilesLoaded) {
        onLoadComplete();
      }
    });
    scene.load.on('filecomplete-image-' + iconPath, () => {
      filesLoaded++;
      if (filesLoaded === targetFilesLoaded) {
        onLoadComplete();
      }
    });
  }

  if (!scene.textures.exists(spriteSheetPath)) {
    targetFilesLoaded++;
    scene.load.spritesheet(
      spriteSheetPath,
      `${customizationsSpritePath}${customization.slot}/${spriteSheetPath}.png`,
      { frameWidth: 64, frameHeight: 64 },
    );
  }
  if (!scene.textures.exists(`${dollViewPath}`)) {
    targetFilesLoaded++;
    scene.load.image(
      `${dollViewPath}`,
      `${customizationsUIPath}${customization.slot}/${dollViewPath}.png`,
    );
  }
  if (!scene.textures.exists(`${iconPath}`)) {
    targetFilesLoaded++;
    scene.load.image(
      `${iconPath}`,
      `${customizationsUIPath}${customization.slot}/${iconPath}.png`,
    );
  }

  scene.load.start();
}

export function getItemRarityTier(itemId: number) {
  const customization = getCustomizationFromId(itemId);
  if (customization === undefined) return null;
  return (Rarities as RarityTier[]).find(
    (rarity: RarityTier) => rarity.tier === customization.tier,
  );
}

export function getPriceForItem(itemId: number) {
  const customization = getCustomizationFromId(itemId);
  if (customization === undefined) return 0;
  if (customization.price !== undefined) return customization.price;
  return getItemRarityTier(itemId)?.price || 0;
}
